import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDSBreadcrumb } from '@eds2-beta1-react/eds-breadcrumb';
import './BreadcrumbNav.css';

//   BreadcrumbNav - Displays breadcrumb navigation for dataset folders and nested folders.
//   Supports truncation, expand/collapse for nested breadcrumbs path, and auto-collapses on navigation.
 
//added this to limit the no of folders can be present in breadcrumb to 9
const MAX_VISIBLE_FOLDERS = 9;

//if name of folder is very long more than 10 char ,truncate the name to upto 10 then end with ... to make it look good in breadcrumb
const truncateLabel = (name) => {
    if (!name) return '';
    return name.length > 10 ? name.substring(0, 10) + "..." : name;
};

/**
 * Breadcrumb navigation for dataset folders.
 * Supports truncation, expand/collapse for nested breadcrumbs path.
 */

const BreadcrumbNav = ({ 
    rootLabel = 'Data',
    datasetName, 
    breadcrumb, 
    currentPath,  
    onNavigateBack, 
    onNavigateToPath 
}) => {
    const [isBreadcrumbExpanded, setIsBreadcrumbExpanded] = useState(false);
    const breadcrumbContainerRef = useRef(null);

    // Auto-collapse when navigating to a new folder
    useEffect(() => {
        
        setIsBreadcrumbExpanded(false);
    }, [breadcrumb?.length]);

    // Reset scroll position to root Data folder whenever breadcrumb collapses
    useEffect(() => {
        if (!isBreadcrumbExpanded && breadcrumbContainerRef.current) {
            breadcrumbContainerRef.current.scrollLeft = 0;
        }
    }, [isBreadcrumbExpanded]);

    // It builds the final array that gets rendered as the breadcrumb UI.
    const enhancedBreadcrumb = useMemo(() => {
        const base = [
            { label: truncateLabel(rootLabel), value: rootLabel, url: '#' },
            { label: truncateLabel(datasetName), value: datasetName, url: '#' }
        ];

        if (!breadcrumb || breadcrumb.length <= 1) return base;
        
        //It takes the breadcrumb array, skips the first item (root), and transforms each remaining item into the format that <EDSBreadcrumb> expects.
        const folderCrumbs = breadcrumb.slice(1).map(crumb => ({
            label: truncateLabel(crumb.name),
            value: crumb.name,
            url: '#'
        }));

        if (!isBreadcrumbExpanded && folderCrumbs.length > MAX_VISIBLE_FOLDERS) {
            
            return [...base, ...folderCrumbs.slice(0, MAX_VISIBLE_FOLDERS)];
        }

        return [...base, ...folderCrumbs];
    }, [breadcrumb, datasetName, isBreadcrumbExpanded, rootLabel]);

   /***
    * It decides whether to show the "/ ..." or "Collapse" button based on how many folders
    * are in the breadcrumb path and whether the breadcrumb is expanded.
    ***/
    const folderCount = breadcrumb ? breadcrumb.length - 1 : 0;
    
    const showEllipsis = !isBreadcrumbExpanded && folderCount > MAX_VISIBLE_FOLDERS;
    
    const showCollapse = isBreadcrumbExpanded && folderCount > MAX_VISIBLE_FOLDERS;
    const handleCollapse = useCallback(() => {
        setIsBreadcrumbExpanded(false);
    }, []);
    // Handle breadcrumb click — navigate to the clicked folder's path.
    // It handles what happens when user clicks on any element in the breadcrumb.
    const handleBreadcrumbNavigation = useCallback((event) => {
        const { detail } = event;
        if (!detail) return;
        const clickedValue = detail.value;
        

        if (clickedValue === rootLabel) {
            
            //clicking on "Data" should always navigate back to root, even if it's not in the breadcrumb (e.g. when viewing a file)
            onNavigateBack();
            return;
        }
    //When user clicks the dataset name in the breadcrumb  go back to to that datasetname 
        if (clickedValue === datasetName) {
            if (currentPath !== '/') {
                onNavigateToPath('/');
            }
            return;
        }


        const breadcrumbItem = breadcrumb.find(item => item.name === clickedValue);
        if (breadcrumbItem && breadcrumbItem.path !== currentPath) {
            onNavigateToPath(breadcrumbItem.path);
        }
    }, [breadcrumb, currentPath, datasetName, onNavigateBack, onNavigateToPath, rootLabel]);

    // Register breadcrumb click listener and apply title attributes to shadow DOM
    //. Listens for breadcrumb clicks.When user clicks any breadcrumb item, the EDS component calls a custom event. This catches it and runs the navigation logic.
    useEffect(() => {
        window.addEventListener('eds-breadcrumb-click', handleBreadcrumbNavigation);

        //  The breadcrumb shows truncated names like name.... User needs to view the full name .it helps in adding title attributes to eaach element so hovering shows full name .
        /* istanbul ignore next -- shadow DOM not testable in jsdom */
        const applyTitles = () => {
            const edsBreadcrumb = breadcrumbContainerRef.current?.querySelector('eds-breadcrumb');
            const shadowRoot = edsBreadcrumb?.shadowRoot;
            if (!shadowRoot) return;
            const links = shadowRoot.querySelectorAll('a');
            links.forEach((link, i) => {
                if (enhancedBreadcrumb[i]) {
                    link.title = enhancedBreadcrumb[i].value;
                }
            });
        };

        const observer = new MutationObserver(applyTitles);
        const edsBreadcrumb = breadcrumbContainerRef.current?.querySelector('eds-breadcrumb');

        if (edsBreadcrumb?.shadowRoot) {
            observer.observe(edsBreadcrumb.shadowRoot, { childList: true, subtree: true });
        }
        applyTitles();

        return () => {
            window.removeEventListener('eds-breadcrumb-click', handleBreadcrumbNavigation);
            observer.disconnect();
        };
    }, [handleBreadcrumbNavigation, enhancedBreadcrumb]);

    return (
        <div className="breadcrumb-nav-wrapper">
            <div
                ref={breadcrumbContainerRef}
                className={`breadcrumb-nav-container ${
                    isBreadcrumbExpanded 
                        ? 'breadcrumb-nav-container--expanded' 
                        : 'breadcrumb-nav-container--collapsed'
                }`}
            >
                <EDSBreadcrumb options={enhancedBreadcrumb} />
            </div>

            {showEllipsis && (
                <button
                    type="button"
                    className="breadcrumb-expand-btn"
                    onClick={() => setIsBreadcrumbExpanded(true)}
                    title="Show all folders"
                >
                    / ...
                </button>
            )}

            {showCollapse && (
                <button
                    type="button"
                    className="breadcrumb-collapse-btn"
                    onClick={handleCollapse}
                    title="Collapse folders"
                >
                    ◀ Collapse
                </button>
            )}
        </div>
    );
};

export default BreadcrumbNav;